/**
 * Supabase 클라이언트 (app 워크스페이스 wrapper)
 *
 * `@tldr/core`의 `getSupabaseClient` singleton을 환경별 옵션과 함께 호출하고,
 * iOS Share Extension용 토큰 동기 리스너만 추가로 부착한다.
 *
 * **중요**: 본 모듈은 SupabaseClient를 **생성하지 않음**. `getSupabaseClient`가
 * singleton을 보장하므로 web/ 워크스페이스가 alias로 app 페이지를 재사용해도
 * SupabaseClient 인스턴스는 1개만 존재한다 (verifier race 차단).
 *
 * 자세한 배경: memory/web-app-supabase-singleton.md
 */

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { getSupabaseClient } from "@tldr/core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { saveTokens, clearToken } from "./tokenBridge";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error("VITE_SUPABASE_URL 환경 변수가 설정되지 않았습니다.");
}

if (!supabaseAnonKey) {
  throw new Error("VITE_SUPABASE_ANON_KEY 환경 변수가 설정되지 않았습니다.");
}

const isNativePlatform = Capacitor.isNativePlatform();
const isIOS = Capacitor.getPlatform() === "ios";

/** 네이티브 환경용 Capacitor Preferences 스토리지 어댑터 */
const capacitorPreferencesStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const result = await Preferences.get({ key });
    return result.value;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await Preferences.remove({ key });
  },
};

/** Supabase 클라이언트 — `@tldr/core` singleton 경유 (인스턴스 1개 보장) */
export const supabase: SupabaseClient = getSupabaseClient({
  url: supabaseUrl,
  key: supabaseAnonKey,
  platform: isNativePlatform ? "native" : "web",
  storage: isNativePlatform ? capacitorPreferencesStorage : undefined,
});

/**
 * iOS Share Extension과 토큰 공유를 위한 Keychain 동기화
 *
 * Supabase Auth 세션 변경 시 Keychain에 토큰을 저장/삭제합니다.
 * iOS에서만 동작합니다.
 */
if (isIOS) {
  supabase.auth.onAuthStateChange(async (event, session) => {
    console.log("[Supabase] Auth state changed:", event);

    try {
      if (session?.access_token && session?.refresh_token) {
        // 로그인 또는 토큰 갱신 시 저장소에 저장 (access + refresh)
        await saveTokens(session.access_token, session.refresh_token);
        console.log("[Supabase] 토큰 쌍 동기화 완료");
      } else {
        // 로그아웃 또는 세션 만료 시 저장소에서 삭제
        await clearToken();
        console.log("[Supabase] 토큰 삭제 완료");
      }
    } catch (error) {
      console.error("[Supabase] 토큰 동기화 실패:", error);
    }
  });
}
